import React, { useRef, useState } from 'react';
import notifyHB from '../../../config/notifyHB';
import { toast } from '@/components/toast/toast';
import { Certificate } from '../../../entities/certificate';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PdfPage, PdfPageHandle } from '../components/pdf-page/pdf-page';
import { SidebarAnnotation } from '../components/annotation/sidebar-annotation';
import { Annotation } from '../components/annotation/annotation';
import { ANNOTATION_TYPES } from '../components/annotation/annotation';
import { PDFDocument } from 'pdf-lib';
import styles from './document-annotator.module.scss';
import { DateTime } from 'luxon';
import { DocumentSignerOption, SIGNER_OPTION_COLORS, SignerEntry } from './utils';
import { AddSignersPanel } from './components/add-signers-panel/add-signers-panel';
import { SignDocumentModal } from '../components/sign-document-modal/sign-document-modal';
import { uploadDocument } from '../../../use-cases/upload-document';
import {
	signUploadedDocument,
	VisibleSignatureOptions,
} from '../../../use-cases/sign-uploaded-document';
import { getSignedDocumentDownloadUrl } from '../../../use-cases/get-signed-document-download-url';
import { sendSignOtp } from '../../../use-cases/send-sign-otp';
import { NewSigner } from '../../../use-cases/add-document-signers';
import { Button } from '@/components/ui/button';
import { Loader2Icon } from 'lucide-react';
import { downloadFromUrl } from '@/utils/download-file';
import { getUserEmail } from '@/utils/get-user-email';
import { getClientDeviceType } from '@/utils/get-client-device-type';
import { isHttpError } from '@/http';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { getUserName } from '@/utils/get-user-name';
import { buildVisibleSignatureOptionsForAnnotation } from '../util/build-visible-signature-options';
import { shouldShowLineageToggle } from '../util/should-show-lineage-toggle';
import { buildLineageOutcome, LineageOutcome } from '../util/lineage-outcome';
import {
	resolvePublicRetrievalRequest,
	shouldShowPublicRetrievalToggle,
} from '../util/public-retrieval-gate';
import { LineageToggle } from './components/lineage-toggle/lineage-toggle';
import { PublicRetrievalToggle } from './components/lineage-toggle/public-retrieval-toggle';
import { readPublicRetrievalAllowed } from '@/entities/user-profile';

type DocumentAnnotatorProps = {
	documentToSign: File | null;
	onDiscardDocument: () => void;
} & (
	| { mode?: 'create' }
	| {
			mode: 'add-signer';
			documentUlid: string;
			onAddSigners: (signers: NewSigner[]) => Promise<void>;
	  }
);



const DocumentAnnotator: React.FC<DocumentAnnotatorProps> = (props) => {
	const { userProfile } = useAuthContext();
	const currentUserEmail = getUserEmail();
	const isAddSignerMode = props.mode === 'add-signer';
	const CURRENT_USER_SIGNER: SignerEntry | null =
		currentUserEmail && !isAddSignerMode
			? {
					value: currentUserEmail,
					label: 'Yo',
					color: SIGNER_OPTION_COLORS[0],
					order: 1,
					expiration: '',
					role: 'signer',
				}
			: null;
	const pdfPageRef = useRef<PdfPageHandle>(null);
	const [annotations, setAnnotations] = useState<Annotation[]>([]);
	const [selectedDocumentSigner, setSelectedDocumentSigner] =
		useState<SignerEntry | null>(CURRENT_USER_SIGNER);
	const [documentSigners, setDocumentSigners] = useState<SignerEntry[]>(
		CURRENT_USER_SIGNER ? [CURRENT_USER_SIGNER] : [],
	);
	const [isSignDocumentModalOpen, setIsSignDocumentModalOpen] =
		useState(false);
	const [isSubmittingNewSigners, setIsSubmittingNewSigners] = useState(false);
	const [isLineagePageEnabled, setIsLineagePageEnabled] = useState(false);
	const [isPublicRetrievalEnabled, setIsPublicRetrievalEnabled] =
		useState(false);
	// The upload-url response is the late, authoritative word on whether this
	// sponsor may use public retrieval; the profile flag is what we have in
	// time to decide whether to show the toggle at all.
	const uploadPublicRetrievalAllowed = useRef<boolean | null>(null);

	const isLineageToggleVisible = shouldShowLineageToggle({
		mode: isAddSignerMode ? 'add-signer' : 'create',
		currentUserEmail,
		signerEmails: documentSigners.map((signer) => signer.value),
		annotationEmails: annotations.map((annotation) => annotation.email),
	});
	// Adding another signer after ticking the box must not leave the flag set.
	const isLineagePageRequested =
		isLineageToggleVisible && isLineagePageEnabled;

	const isPublicRetrievalToggleVisible = shouldShowPublicRetrievalToggle({
		isLineageToggleVisible,
		isLineagePageEnabled,
		publicRetrievalAllowed: readPublicRetrievalAllowed(userProfile),
	});

	const handleUploadAnnotatedDocument = async (
		_selectedCertificateSerial: Certificate['certificate_id'],
	) => {
		if (props.documentToSign) {
			try {
				const uploadedDocumentInfo = await uploadDocument(
					props.documentToSign,
				);
				uploadPublicRetrievalAllowed.current =
					uploadedDocumentInfo.publicRetrievalAllowed;
				return uploadedDocumentInfo;
			} catch (error) {
				console.debug(error);
				notifyHB('sign-document', 'DocumentAnnotator', error);
			}
		}
	};

	const handleSignUploadedAnnotatedDocumentWithOtp = async (
		documentUlid: string,
		certificateId: Certificate['certificate_id'],
		otp: string,
		otpType: Parameters<typeof signUploadedDocument>[5],
	): Promise<LineageOutcome | null | undefined> => {
		if (props.documentToSign) {
			try {
				const signerManuscriptAnnotations = annotations.filter(
					(a) =>
						a.type === 'manuscript' &&
						a.email === currentUserEmail,
				);

				let visibleSignatureOptions:
					| VisibleSignatureOptions[]
					| undefined;
				if (signerManuscriptAnnotations.length > 0) {
					const pdfBytes = await props.documentToSign.arrayBuffer();
					const pdfDoc = await PDFDocument.load(pdfBytes);
					const userName = getUserName(userProfile) ?? 'XXXXX';

					visibleSignatureOptions = signerManuscriptAnnotations.map(
						(annotation) =>
							buildVisibleSignatureOptionsForAnnotation(
								annotation,
								pdfDoc,
								userName,
							),
					);
				}

				const isPublicRetrievalRequested =
					resolvePublicRetrievalRequest({
						isPublicRetrievalToggleVisible,
						isPublicRetrievalEnabled,
						uploadPublicRetrievalAllowed:
							uploadPublicRetrievalAllowed.current,
					});

				const signDocumentReponse = await signUploadedDocument(
					documentUlid,
					props.documentToSign.name,
					certificateId,
					otp,
					annotations,
					otpType,
					visibleSignatureOptions,
					isLineagePageRequested,
					isPublicRetrievalRequested,
				);

				const lineageOutcome = buildLineageOutcome(
					isLineagePageRequested,
					isPublicRetrievalRequested,
					signDocumentReponse,
				);

				const signedDocumentDownloadUrl =
					await getSignedDocumentDownloadUrl(
						signDocumentReponse.signedPath,
					);

				if (signedDocumentDownloadUrl) {
					await downloadFromUrl(
						signedDocumentDownloadUrl,
						props.documentToSign.name,
					);
					return lineageOutcome;
				} else {
					notifyHB(
						'sign-document',
						'DocumentAnnotator',
						'Failed to sign document. SignedDocumentDownloadUrl is not defined',
					);
					throw new Error('SignedDocumentDownloadUrl is not defined');
				}
			} catch (error) {
				console.debug(error);
				if (isHttpError(error) && error.status === 400) {
					toast({ mode: 'caution', description: `El ${otpType === 'sms' ? 'OTP' : 'PIN'} ingresado no es válido` });
				} else {
					notifyHB('sign-document', 'DocumentAnnotator', error);
					toast({ mode: 'destructive', description: 'No es posible firmar. Intenta de nuevo más tarde' });
				}
				throw error;
			}
		}
	};

	const handleAddSigner = (signer: Omit<SignerEntry, 'color'>) => {
		const color =
			SIGNER_OPTION_COLORS[
				documentSigners.length % SIGNER_OPTION_COLORS.length
			];
		const newSigner: SignerEntry = { ...signer, color };
		setDocumentSigners((current) => [...current, newSigner]);
		setSelectedDocumentSigner((current) => current ?? newSigner);
	};

	const handleRemoveSigner = (signerValue: string) => {
		setDocumentSigners((current) =>
			current.filter((s) => s.value !== signerValue),
		);
		setAnnotations((current) =>
			current.filter((a) => a.email !== signerValue),
		);
		if (selectedDocumentSigner?.value === signerValue) {
			setSelectedDocumentSigner(CURRENT_USER_SIGNER);
		}
	};

	const handleReorderSigners = (fromIndex: number, toIndex: number) => {
		setDocumentSigners((current) => {
			const updated = [...current];
			const [moved] = updated.splice(fromIndex, 1);
			updated.splice(toIndex, 0, moved);
			return updated.map((signer, index) => ({
				...signer,
				order: index + 1,
			}));
		});
	};

	const handleSubmitNewSigners = async () => {
		if (props.mode !== 'add-signer') return;

		const newSigners: NewSigner[] = documentSigners.map(
			(signer, index) => ({
				order: index + 1,
				email: signer.value,
				role: 'SIGNER',
				components: annotations.filter(
					(annotation) => annotation.email === signer.value,
				),
			}),
		);

		if (newSigners.length === 0) {
			toast({
				mode: 'caution',
				description: 'Agrega al menos un firmante',
			});
			return;
		}

		setIsSubmittingNewSigners(true);
		try {
			await props.onAddSigners(newSigners);
		} catch (error) {
			console.debug(error);
			notifyHB('sign-document', 'DocumentAnnotator', error);
			toast({
				mode: 'destructive',
				description:
					'No fue posible agregar el firmante. Intenta de nuevo más tarde',
			});
		} finally {
			setIsSubmittingNewSigners(false);
		}
	};

	const responsiveClassnameSuffix =
		getClientDeviceType() !== 'desktop' ? '-responsive' : '';

	return (
		<div className={styles['document-signature']}>
			<div className={styles['header' + responsiveClassnameSuffix]}>
				<div
					className={
						styles['document-name' + responsiveClassnameSuffix]
					}
				>
					{props.documentToSign?.name}
				</div>
				<div
					className={
						styles['header-buttons' + responsiveClassnameSuffix]
					}
				>
					<Button
						variant="ghost"
						onClick={() => {
							setAnnotations([]);
							props.onDiscardDocument();
						}}
						color="primary"
					>
						Descartar
					</Button>

					{isAddSignerMode ? (
						<Button
							variant="default"
							data-tour="annotation-send-btn"
							onClick={handleSubmitNewSigners}
							disabled={isSubmittingNewSigners}
						>
							Enviar solicitud
							{isSubmittingNewSigners && (
								<Loader2Icon className="animate-spin" />
							)}
						</Button>
					) : (
						<SignDocumentModal
							isOpen={isSignDocumentModalOpen}
							onSign={handleSignUploadedAnnotatedDocumentWithOtp}
							onClose={() => setIsSignDocumentModalOpen(false)}
							onOpen={() => setIsSignDocumentModalOpen(true)}
							uploadDocument={handleUploadAnnotatedDocument}
							onCertificateSelect={sendSignOtp}
						/>
					)}
				</div>
			</div>
			<div className={styles['helper' + responsiveClassnameSuffix]}>
				Toca en cualquier parte sobre el documento para colocar tu
				manuscrito.
			</div>

			<DndProvider backend={HTML5Backend}>
				<div className={styles['builder' + responsiveClassnameSuffix]}>
					<div
						data-tour="annotation-canvas"
						className={
							styles['selected-page' + responsiveClassnameSuffix]
						}
					>
						{props.documentToSign && (
							<PdfPage
								currentEmail={selectedDocumentSigner?.value ?? ''}
								userName={getUserName(userProfile) ?? ''}
								file={props.documentToSign}
								handleRef={pdfPageRef}
								thumbnailsTourId="annotation-page-thumbs"
								documentSigners={documentSigners} //TODO:
								annotations={annotations}
								onDropNote={(annotation) => {
									setAnnotations((oldAnnotations) => [
										...oldAnnotations,
										annotation,
									]);
								}}
								onUpdate={(id, params) => {
									setAnnotations((oldAnnotations) =>
										oldAnnotations.map((annotation) =>
											annotation.id === id
												? { ...params, id }
												: annotation,
										),
									);
								}}
								onRemoveNote={(annotationId: string) => {
									setAnnotations((oldAnnotations) =>
										oldAnnotations.filter(
											(annotation) =>
												annotation.id !== annotationId,
										),
									);
								}}
							/>
						)}
					</div>
					<div
						data-tour="annotation-sidebar"
						className={
							styles['options' + responsiveClassnameSuffix]
						}
					>
<AddSignersPanel
						signers={documentSigners}
						selectedSignerValue={selectedDocumentSigner?.value ?? null}
						annotations={annotations}
						onAddSigner={handleAddSigner}
						onRemoveSigner={handleRemoveSigner}
						onReorderSigners={handleReorderSigners}
						fixedFirstSigner={!isAddSignerMode}
						onSelectSigner={(signerValue) => {
							const signer = documentSigners.find(
								(s) => s.value === signerValue,
							);
							if (signer) setSelectedDocumentSigner(signer);
						}}
					/>

						{isLineageToggleVisible && (
							<LineageToggle
								checked={isLineagePageEnabled}
								onCheckedChange={setIsLineagePageEnabled}
							>
								{isPublicRetrievalToggleVisible && (
									<PublicRetrievalToggle
										checked={isPublicRetrievalEnabled}
										onCheckedChange={
											setIsPublicRetrievalEnabled
										}
									/>
								)}
							</LineageToggle>
						)}

						<div
							style={{
								display: 'flex',
								flexDirection: 'row',
								gap: 8,
								flexWrap: 'wrap',
							}}
						>
							{ANNOTATION_TYPES.filter(
								(annotationType) =>
									annotationType === 'manuscript',
							).map((annotationType) => (
								<div
									key={annotationType + '-tour'}
									data-tour="annotation-type-signature"
								>
									<SidebarAnnotation
										key={annotationType}
										annotationType={annotationType}
										isDragDisabled={false} //TODO: disable drag based on whether the cert. has a manuscript
										onDrop={(type, clientX, clientY) =>
											pdfPageRef.current?.dropAnnotationAt(
												type,
												clientX,
												clientY,
											)
										}
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</DndProvider>
		</div>
	);
};

export default DocumentAnnotator;
